import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  BarChart3,
  BookOpen,
  CheckCircle2,
  ClipboardList,
  Eye,
  FilePen,
  FileSpreadsheet,
  LayoutGrid,
  MoreVertical,
  Pencil,
  Search,
  Send,
  Timer,
  Users,
  X,
} from 'lucide-react';

import { Badge } from '@/components/ui/Badge';
import { CheckboxGroup } from '@/components/ui/CheckboxGroup';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { DatePicker } from '@/components/ui/DatePicker';
import { EmptyState } from '@/components/ui/EmptyState';
import { FormToggleButton } from '@/components/ui/FormToggleButton';
import { Input } from '@/components/ui/Input';
import { Menu } from '@/components/ui/Menu';
import { MetricTile } from '@/components/ui/MetricTile';
import { Pagination } from '@/components/ui/Pagination';
import { PercentRing } from '@/components/ui/PercentRing';
import { QueryError } from '@/components/ui/QueryError';
import { Select } from '@/components/ui/Select';
import { Table, type Column } from '@/components/ui/Table';
import { Tabs, tabPanelId } from '@/components/ui/Tabs';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { ApiRequestError } from '@/lib/apiClient';
import { applyFieldErrors } from '@/lib/forms';
import { formatDate, formatTimeRange } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { Exam, ExamListParams, ExamOrigin, ExamPublication, ExamType } from '@/types/exam';
import { examsApi } from '../api/exams.api';
import {
  EXAM_SESSION_LABEL,
  EXAM_TYPE_FILTER_OPTIONS,
  EXAM_TYPE_HINT,
  EXAM_TYPE_LABEL,
  EXAM_TYPE_OPTIONS,
  earliestExamDate,
} from '../exams/constants';
import {
  EXAM_STATUS_DOT,
  EXAM_STATUS_FILTERS,
  EXAM_STATUS_HINT,
  EXAM_STATUS_LABEL,
  EXAM_STATUS_TAB_HINT,
  EXAM_STATUS_TAB_LABEL,
  EXAM_STATUS_TEXT,
  examStatus,
  type ExamStatus,
  isPastPaper,
  type ExamStatusFilter,
} from '../exams/status';
import { emptyExamForm, examFormResolver, examSchema, type ExamFormValues } from '../exams/schema';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { useExamClock } from '../exams/useExamClock';
import type { AllocatedClass, TaughtClass } from '../types/teacher';
import {
  distinctSubjects,
  examOriginFrom,
  formatInstituteDate,
  instituteToday,
  setExamOrigin,
  withExamOrigin,
} from '../utils';
import { useLiveFormErrors } from '@/hooks/useLiveFormErrors';

interface TeacherMarksSectionProps {
  currentClass: TaughtClass;
  /** Passed down rather than fetched again — the dashboard already has them. */
  allocatedClasses: TaughtClass[];
  /**
   * The sections they are class teacher of. They read every paper set there,
   * but mark only the subjects they teach — the rest open as View marks.
   */
  classTeacherSections: AllocatedClass[];
}

/** One line of the table: a paper, with where it stands and where it sits. */
interface PaperRow extends Exam {
  status: ExamStatus;
  /** Its place in the whole filtered list, not on the page being shown. */
  serial: number;
}

/** How many papers a page of the table holds, and what else can be chosen. */
const PAGE_SIZES = [10, 25, 50] as const;

/**
 * The table's filters, as they sit in the address bar, so opening a sheet and
 * coming Back finds the same class, tab, search and page.
 *
 * `classSectionUuid` is empty until the teacher picks one; the screen then reads
 * its own default (see `defaultSectionUuid`). Not `class` — the dashboard
 * around this section already reads that name as the console's active class.
 * The origin tab is not here: it has always travelled as `origin`, and the
 * results page and the sheet link back with it set.
 */
type Filters = {
  classSectionUuid: string;
  subjectUuid: string;
  examType: ExamType | '';
  examDate: string;
  status: ExamStatusFilter;
  search: string;
  page: number;
  /** Rows per page — with the page number, or a restored page shows other rows. */
  limit: number;
};

const DEFAULT_FILTERS: Filters = {
  classSectionUuid: '',
  subjectUuid: '',
  examType: '',
  examDate: '',
  status: 'CURRENT',
  search: '',
  page: 1,
  limit: PAGE_SIZES[0],
};

/** Section and subject are uuids the server scopes; only the fixed sets are checked here. */
const FILTER_OPTIONS = {
  allowed: {
    examType: EXAM_TYPE_OPTIONS.map((option) => option.value),
    status: EXAM_STATUS_FILTERS,
    limit: PAGE_SIZES,
  },
};

/** Reset with the origin tab, in the same write that changes it. */
const RESET_WITH_ORIGIN = ['status', 'page'] as const satisfies readonly (keyof Filters)[];

/** A mark as a share of what the paper was out of, to one decimal. */
function sharePercent(marks: number | null, outOf: number): number | null {
  return marks === null || outOf <= 0 ? null : Math.round((marks / outOf) * 1000) / 10;
}

/** A figure the table has nothing behind yet. Never a nought, which is an answer. */
const NOT_YET = <span className="text-ink-3">—</span>;

/**
 * The papers this teacher has set, and how far each one is marked.
 *
 * Everything comes from `GET /institute/exams`, which returns only the sections
 * this caller is allotted — so the list is theirs by construction rather than
 * by a filter that could be removed.
 *
 * The class, subject, kind and date filters are the server's; the tab strip and
 * the search box narrow what has already arrived, because a standing is worked
 * out here (see `exams/status.ts`) and the server has no column to filter it by.
 * The figures across the top count the list the server returned, so they keep
 * describing the whole class while a tab shows three rows of it.
 *
 * Entering marks happens on its own screen: a sheet is a table of forty rows
 * that has to be saved as one thing, and squeezing it into a card next to five
 * other papers is how a teacher saves the wrong class's marks.
 */
function compareExamsHeld(
  a: { examDate: string; startTime?: string | null; name: string },
  b: { examDate: string; startTime?: string | null; name: string },
) {
  const dateDiff = a.examDate.localeCompare(b.examDate);
  if (dateDiff !== 0) return dateDiff;

  if (a.startTime && b.startTime) {
    const timeDiff = a.startTime.localeCompare(b.startTime);
    if (timeDiff !== 0) return timeDiff;
  } else if (a.startTime && !b.startTime) {
    return -1;
  } else if (!a.startTime && b.startTime) {
    return 1;
  }

  return a.name.localeCompare(b.name);
}

export function TeacherMarksSection({
  currentClass,
  allocatedClasses,
  classTeacherSections,
}: TeacherMarksSectionProps) {
  const navigate = useNavigate();

  // Read once per mount: a component that asks for the date on every render
  // would change a paper's standing mid-session at midnight, under the reader.
  const today = useMemo(() => instituteToday(), []);

  /** Every section the list can show — one entry each, however many subjects they take there. */
  const sections = useMemo(
    () => [
      ...new Map(
        [...allocatedClasses, ...classTeacherSections].map((c) => [c.classSectionUuid, c]),
      ).values(),
    ],
    [allocatedClasses, classTeacherSections],
  );

  /**
   * The list opens on the section they are class teacher of — the one whose
   * results are theirs to answer for — or their first class when they have none.
   */
  const defaultSectionUuid =
    classTeacherSections[0]?.classSectionUuid ?? sections[0]?.classSectionUuid ?? '';

  const [filters, setFilters] = useUrlFilters(DEFAULT_FILTERS, FILTER_OPTIONS);
  const { status: statusTab, search, page } = filters;

  // Read rather than stored, so allotments that arrive after this mounts are
  // followed in, and a section from an old link that is no longer theirs falls
  // back to the default instead of listing nothing.
  const classSectionUuid = sections.some((c) => c.classSectionUuid === filters.classSectionUuid)
    ? filters.classSectionUuid
    : defaultSectionUuid;

  const listParams: ExamListParams = {
    classSectionUuid,
    subjectUuid: filters.subjectUuid,
    examType: filters.examType,
    examDate: filters.examDate,
  };

  // Nothing is read until a class is chosen: there is no "all classes" list any more.
  const { data, isLoading, error, refetch } = useApiQuery(
    () => (classSectionUuid ? examsApi.list(listParams) : Promise.resolve([])),
    [classSectionUuid, filters.subjectUuid, filters.examType, filters.examDate],
  );

  // A separate read, under the same filters, so the figures across the top keep
  // describing live papers only — a withdrawn paper is not one the class sits.
  const {
    data: suspendedData,
    isLoading: isSuspendedLoading,
    error: suspendedError,
    refetch: refetchSuspended,
  } = useApiQuery(
    () =>
      classSectionUuid
        ? examsApi.list({ ...listParams, status: 'suspended' })
        : Promise.resolve([]),
    [classSectionUuid, filters.subjectUuid, filters.examType, filters.examDate],
  );

  // Re-read when the next live paper ends, so a paper sat this afternoon moves
  // from "Scheduled" to "Marks pending" at its end time without a reload. A
  // suspended paper reads as suspended whatever the clock says.
  const now = useExamClock(data ?? []);

  /**
   * Class tests the teachers set, or the institute's own exams. Split client-side
   * from the one read, so both badges count without a second request. Read from
   * the address (`examOriginFrom`), so coming back from the class results or a
   * sheet reopens the tab the teacher left.
   */
  const currentUser = useAuthStore((state) => state.user);
  const [searchParams, setSearchParams] = useSearchParams();
  const originTab = examOriginFrom(searchParams);

  const [isFormOpen, setIsFormOpen] = useState(false);
  /** The paper being corrected, when the form is open to edit rather than to set. */
  const [editing, setEditing] = useState<Exam | null>(null);
  const [deleting, setDeleting] = useState<Exam | null>(null);
  /** Which of the form's two submit buttons is saving, so only that one spins. */
  const [savingAs, setSavingAs] = useState<ExamPublication | null>(null);
  /** The draft whose Publish was chosen, while the request is in flight. */
  const [publishing, setPublishing] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // The class is always set, so it only counts as a filter once moved off the default.
  const hasFilters = Boolean(
    classSectionUuid !== defaultSectionUuid ||
    filters.subjectUuid ||
    filters.examType ||
    filters.examDate,
  );

  /** Every paper the server returned, each with the standing read off it. */
  const papers = useMemo(
    () =>
      (data ?? [])
        .filter((exam) => exam.origin === originTab)
        .map((exam) => ({ ...exam, status: examStatus(exam, now) }))
        .sort(compareExamsHeld),
    [data, now, originTab],
  );

  const suspendedPapers = useMemo(
    () =>
      (suspendedData ?? [])
        .filter((exam) => exam.origin === originTab)
        .map((exam) => ({ ...exam, status: examStatus(exam, now) }))
        .sort(compareExamsHeld),
    [suspendedData, now, originTab],
  );

  /** Live papers under each origin tab, for the badges. */
  const originCounts = useMemo(() => {
    const counts: Record<ExamOrigin, number> = { CLASS_TEST: 0, INSTITUTE: 0 };
    for (const exam of data ?? []) counts[exam.origin] += 1;
    return counts;
  }, [data]);

  function changeOrigin(origin: ExamOrigin) {
    // `replace`: switching tabs is not a step Back should have to undo. The
    // standing tab and page go back to their defaults in this same write — a
    // second write from this handler would start from the old address and undo it.
    setSearchParams(
      (current) => {
        const next = setExamOrigin(current, origin);
        for (const key of RESET_WITH_ORIGIN) next.delete(key);
        return next;
      },
      { replace: true },
    );
    // The form sets class tests; it has no place under the institute's exams.
    if (origin === 'INSTITUTE') closeForm();
  }

  const isSuspendedTab = statusTab === 'SUSPENDED';
  /** The list the current tab reads from. */
  const listed = isSuspendedTab ? suspendedPapers : papers;

  const classOptions = useMemo(
    () => sections.map((c) => ({ value: c.classSectionUuid, label: c.className })),
    [sections],
  );

  /**
   * Whether they teach this paper's subject in its section. Only then are the
   * marks theirs to enter; a class teacher reading another subject's paper gets
   * the sheet read-only, which the server decides again (`canEnterMarks`).
   */
  function teachesPaper(paper: Exam) {
    return allocatedClasses.some(
      (c) => c.classSectionUuid === paper.classSection.uuid && c.subjectUuid === paper.subject.uuid,
    );
  }

  function marksLabel(paper: PaperRow) {
    // A paper still being written says Enter marks too: the marks are this
    // teacher's to give, and the sheet itself explains why it is not open yet.
    if (!teachesPaper(paper)) return 'View marks';
    return paper.status === 'MARKED' ? 'Review marks' : 'Enter marks';
  }

  const subjectOptions = useMemo(
    () => [
      { value: '', label: 'All subjects' },
      ...distinctSubjects(allocatedClasses).map((subject) => ({
        value: subject.uuid,
        label: subject.name,
      })),
    ],
    [allocatedClasses],
  );

  /** How many children the papers on screen are set for, counted once a section. */
  const rosterSize = useMemo(() => {
    const bySection = new Map(
      sections
        .filter((c) => !classSectionUuid || c.classSectionUuid === classSectionUuid)
        .map((c) => [c.classSectionUuid, c.totalStudents]),
    );

    return [...bySection.values()].reduce((sum, count) => sum + count, 0);
  }, [sections, classSectionUuid]);

  /** How many papers sit under each tab, so the strip can say so. */
  const statusCounts = useMemo(() => {
    const counts: Record<ExamStatusFilter, number> = {
      CURRENT: 0,
      PAST: 0,
      SUSPENDED: suspendedPapers.length,
      DRAFT: 0,
      MARKED: 0,
      MARKING: 0,
      SCHEDULED: 0,
    };
    for (const paper of papers) {
      counts[paper.status] += 1;
      counts[isPastPaper(paper, paper.status, today) ? 'PAST' : 'CURRENT'] += 1;
    }

    return counts;
  }, [papers, suspendedPapers, today]);

  /**
   * The class's standing across every paper listed.
   *
   * Each paper's average is a share of its own total before they are averaged
   * together — a 20/25 and a 70/100 are not the same performance, and adding
   * the raw marks would let a bigger paper quietly outvote the rest.
   */
  const averageScore = useMemo(() => {
    const shares = papers
      .map((paper) => sharePercent(paper.averageMarks, paper.maxMarks))
      .filter((share): share is number => share !== null);

    if (shares.length === 0) return null;

    return {
      percent: Math.round(shares.reduce((sum, share) => sum + share, 0) / shares.length),
      papers: shares.length,
    };
  }, [papers]);

  /** What the table shows: the tab, then the search box, over the same list. */
  const rows = useMemo<PaperRow[]>(() => {
    const needle = search.trim().toLowerCase();

    return listed
      .filter((paper) => {
        if (statusTab === 'CURRENT') return !isPastPaper(paper, paper.status, today);
        if (statusTab === 'PAST') return isPastPaper(paper, paper.status, today);
        return paper.status === statusTab;
      })
      .filter(
        (paper) =>
          !needle ||
          paper.name.toLowerCase().includes(needle) ||
          paper.subject.name.toLowerCase().includes(needle) ||
          paper.classSection.displayName.toLowerCase().includes(needle) ||
          (paper.createdBy?.fullName && paper.createdBy.fullName.toLowerCase().includes(needle)),
      )
      // Past papers newest first: the one just finished is the one looked for.
      .sort((a, b) => (statusTab === 'PAST' ? compareExamsHeld(b, a) : compareExamsHeld(a, b)))
      .map((paper, index) => ({ ...paper, serial: index + 1 }));
  }, [listed, statusTab, search, today]);

  const totalPages = Math.max(1, Math.ceil(rows.length / filters.limit));
  // Clamped rather than stored, so narrowing the list from page four cannot
  // leave the table on a page that no longer exists.
  const currentPage = Math.min(page, totalPages);
  const pageRows = rows.slice((currentPage - 1) * filters.limit, currentPage * filters.limit);

  /**
   * Any change to what is being listed starts the reader at the top again.
   * Done where the change is made rather than in an effect, which would also
   * fire on arrival and throw away a page restored from the address.
   */
  function updateFilters(patch: Partial<Filters>) {
    setFilters({ ...patch, page: patch.page ?? 1 });
  }

  function changePageSize(size: number) {
    setFilters({ limit: size, page: 1 });
  }

  const form = useForm<ExamFormValues>({
    resolver: examFormResolver,
    defaultValues: emptyExamForm(currentClass.classSectionUuid, currentClass.subjectUuid),
  });
  useLiveFormErrors(form);
  const {
    formState: { isSubmitting, errors },
    register,
    watch,
  } = form;
  const selectedType = watch('examType');
  const formSubject = watch('subjectUuid');

  /**
   * The sections a new paper is set for. Kept beside the form rather than in
   * it: the shared exam schema is one section per paper, which is what the
   * server stores. The form's own `classSectionUuid` follows the first one
   * ticked, so that schema still has a section to check.
   */
  const [formSections, setFormSections] = useState<string[]>([]);

  /** The subjects they may set a paper in — one entry each. */
  const formSubjectOptions = useMemo(
    () =>
      distinctSubjects(allocatedClasses).map((subject) => ({
        value: subject.uuid,
        label: subject.name,
      })),
    [allocatedClasses],
  );

  /** Every section they teach the chosen subject in — and nowhere else. */
  const formSectionOptions = useMemo(
    () =>
      allocatedClasses
        .filter((c) => c.subjectUuid === formSubject)
        .map((c) => ({ value: c.classSectionUuid, label: c.className, grade: c.grade }))
        .sort((a, b) => a.label.localeCompare(b.label, 'en-IN', { numeric: true })),
    [allocatedClasses, formSubject],
  );

  /** "All of Class 8" — offered only where a class has more than one of their sections. */
  const wholeClassShortcuts = useMemo(() => {
    const byGrade = new Map<string, string[]>();
    for (const option of formSectionOptions) {
      byGrade.set(option.grade, [...(byGrade.get(option.grade) ?? []), option.value]);
    }
    return [...byGrade.entries()]
      .filter(([, uuids]) => uuids.length > 1)
      .map(([grade, uuids]) => ({ grade, uuids }));
  }, [formSectionOptions]);

  function chooseSections(uuids: string[]) {
    setFormSections(uuids);
    form.setValue('classSectionUuid', uuids[0] ?? '', {
      shouldValidate: form.formState.isSubmitted,
    });
  }

  // A subject change keeps only the ticked sections that still teach it.
  useEffect(() => {
    if (editing) return;
    setFormSections((current) => {
      const allowed = current.filter((uuid) =>
        formSectionOptions.some((option) => option.value === uuid),
      );
      form.setValue('classSectionUuid', allowed[0] ?? '');
      return allowed;
    });
  }, [formSectionOptions, editing, form]);

  /**
   * Opens a blank form on the class the list is showing, when they teach the
   * subject there; otherwise on the class picked at the top of the console.
   */
  function openNewPaper() {
    const sectionUuid = allocatedClasses.some(
      (c) =>
        c.classSectionUuid === classSectionUuid &&
        c.subjectUuid === currentClass.subjectUuid,
    )
      ? classSectionUuid
      : currentClass.classSectionUuid;
    form.reset(emptyExamForm(sectionUuid, currentClass.subjectUuid));
    setFormSections([sectionUuid]);
    setIsFormOpen(true);
  }

  function closeForm() {
    setIsFormOpen(false);
    setEditing(null);
    form.reset(emptyExamForm(currentClass.classSectionUuid, currentClass.subjectUuid));
  }

  /** Opens the one form, filled with the paper as it stands. */
  function startEditing(paper: Exam) {
    form.reset({
      classSectionUuid: paper.classSection.uuid,
      subjectUuid: paper.subject.uuid,
      name: paper.name,
      examType: paper.examType,
      maxMarks: String(paper.maxMarks),
      // A pass mark the paper only inherits is shown as the empty box that
      // means "institute default", so saving does not pin today's figure to it.
      passMarks: paper.passMarksFromInstituteDefault ? '' : String(paper.passMarks),
      examDate: paper.examDate,
    });
    setEditing(paper);
    setIsFormOpen(true);
  }

  // Edit is chosen from a row far down the list; the form opens at the top of
  // the page. Scrolled once it has rendered, which the click itself is too early for.
  useEffect(() => {
    if (!editing) return;
    document
      .getElementById('set-paper-form')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [editing]);

  const isDraft = editing?.publication === 'DRAFT';
  const failure = editing ? 'Could not update this paper' : 'Could not set this paper';

  const onSubmit = form.handleSubmit(async (values, event) => {
    // Both buttons submit the form, so validation guards both; the one pressed
    // says whether this is a draft.
    const submitter =
      event?.nativeEvent instanceof SubmitEvent ? event.nativeEvent.submitter : null;
    const publication: ExamPublication =
      submitter instanceof HTMLButtonElement && submitter.value === 'DRAFT' ? 'DRAFT' : 'PUBLISHED';
    const isPublishing = isDraft && publication === 'PUBLISHED';
    setSavingAs(publication);

    try {
      const parsed = examSchema.parse(values);
      const saved = editing
        ? await examsApi.update(editing.uuid, {
            // The class and subject are not editable, and the server refuses
            // them, so the changes are listed rather than spread.
            name: parsed.name,
            examType: parsed.examType,
            maxMarks: parsed.maxMarks,
            passMarks: parsed.passMarks,
            examDate: parsed.examDate,
            // Only a draft being released says so; a draft saved again stays one.
            ...(isPublishing && { publication: 'PUBLISHED' as const }),
          })
        : null;

      if (!saved) {
        // A new paper: one per ticked section, set together or not at all.
        const { classSectionUuid: _first, ...paper } = parsed;
        const set = await examsApi.createBatch({
          ...paper,
          classSectionUuids: formSections,
          publication,
        });
        const sections = set.map((exam) => exam.classSection.displayName).join(', ');
        toast.success(
          publication === 'DRAFT' ? `${parsed.name} is saved as a draft` : `${parsed.name} is set`,
          publication === 'DRAFT'
            ? `For ${sections}. Only you can see it until you publish it.`
            : `${sections} · out of ${parsed.maxMarks} · ${formatDate(parsed.examDate)}`,
        );
        closeForm();
        refetch();
        return;
      }

      toast.success(
        saved.publication === 'DRAFT'
          ? `${saved.name} is saved as a draft`
          : editing && !isPublishing
            ? `${saved.name} is updated`
            : `${saved.name} is set`,
        saved.publication === 'DRAFT'
          ? 'Only you can see it. Publish it when it is ready.'
          : `${saved.classSection.displayName} · out of ${saved.maxMarks} · ${formatDate(saved.examDate)}`,
      );
      closeForm();
      refetch();
    } catch (caught) {
      if (caught instanceof ApiRequestError) {
        applyFieldErrors(form.setError, caught);
        toast.error(failure, caught.message);
        return;
      }
      toast.error(failure, 'Something went wrong. Please try again.');
    } finally {
      setSavingAs(null);
    }
  });

  /** Releases a draft as it stands, straight from its row. */
  async function publish(paper: Exam) {
    setPublishing(paper.uuid);
    try {
      const saved = await examsApi.update(paper.uuid, { publication: 'PUBLISHED' });
      toast.success(
        `${saved.name} is set`,
        `${saved.classSection.displayName} · out of ${saved.maxMarks} · ${formatDate(saved.examDate)}`,
      );
      refetch();
    } catch (caught) {
      toast.error(
        `Could not publish ${paper.name}`,
        caught instanceof ApiRequestError ? caught.message : 'Please try again.',
      );
    } finally {
      setPublishing(null);
    }
  }

  async function confirmDelete() {
    if (!deleting) return;
    setIsDeleting(true);

    try {
      await examsApi.remove(deleting.uuid);
      toast.success(
        `${deleting.name} has been withdrawn`,
        'The marks recorded against it are kept.',
      );
      setDeleting(null);
      refetch();
      refetchSuspended();
    } catch (caught) {
      setDeleting(null);
      toast.error(
        `Could not withdraw ${deleting.name}`,
        caught instanceof ApiRequestError ? caught.message : 'Please try again.',
      );
    } finally {
      setIsDeleting(false);
    }
  }

  function openSheet(paper: Exam) {
    navigate(withExamOrigin(`/teacher/exams/${paper.uuid}/marks`, originTab));
  }

  const columns: Column<PaperRow>[] = [
    {
      key: 'serial',
      header: '#',
      align: 'center',
      className: 'w-12',
      headerClassName: 'w-12',
      render: (row) => <span className="text-xs font-semibold text-ink-3">{row.serial}</span>,
    },
    {
      key: 'name',
      header: 'Paper name',
      render: (row) => {
        const isSelf = Boolean(
          row.isMine ||
          (currentUser && row.createdBy?.fullName && (
            row.createdBy.fullName.trim().toLowerCase() === currentUser.fullName?.trim().toLowerCase() ||
            row.createdBy.fullName.trim().toLowerCase() === `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim().toLowerCase()
          ))
        );

        return (
          <div className="min-w-0">
            <p className="truncate text-sm font-bold text-ink capitalize" title={row.name}>
              {row.name}
            </p>
            <div className="flex items-center gap-1.5 text-xs font-semibold text-ink-3">
              <span className="truncate">{row.classSection.displayName}</span>
              {row.origin === 'INSTITUTE' && (
                <>
                  <span>·</span>
                  {isSelf ? (
                    <span className="font-extrabold text-blue-600 dark:text-blue-400">YOU</span>
                  ) : (
                    <span className="truncate">{row.createdBy?.fullName || 'Teacher'}</span>
                  )}
                </>
              )}
            </div>
          </div>
        );
      },
    },
    {
      key: 'subject',
      header: 'Subject',
      render: (row) => (
        <span className="flex min-w-0 items-center gap-2">
          <span
            className="grid h-7 w-7 shrink-0 place-items-center rounded-[8px] border border-violet-500/25 bg-violet-500/15 text-violet-600 dark:text-violet-400"
            aria-hidden
          >
            <BookOpen className="h-3.5 w-3.5" />
          </span>
          <span className="truncate text-sm font-semibold text-ink-2 capitalize">{row.subject.name}</span>
        </span>
      ),
    },
    {
      key: 'kind',
      header: 'Kind',
      hideOnMobile: true,
      render: (row) => (
        <span className="whitespace-nowrap text-sm font-medium text-ink-2">
          {EXAM_TYPE_LABEL[row.examType]}
        </span>
      ),
    },
    {
      key: 'maxMarks',
      header: 'Out of',
      align: 'center',
      hideOnMobile: true,
      render: (row) => (
        <span className="text-sm font-bold tabular-nums text-ink">{row.maxMarks}</span>
      ),
    },
    {
      key: 'examDate',
      header: 'Held on',
      hideOnMobile: true,
      render: (row) => (
        <span className="whitespace-nowrap text-sm font-medium text-ink-2">
          {formatInstituteDate(row.examDate)}
        </span>
      ),
    },
    {
      key: 'time',
      header: 'Time',
      hideOnMobile: true,
      // The session is the server's, read off the start time — never worked
      // out here, so it cannot disagree with the exam timetable.
      render: (row) =>
        row.session === null ? (
          <span className="whitespace-nowrap text-xs font-medium text-ink-3">Not set</span>
        ) : (
          <div className="flex flex-col items-start gap-1">
            <Badge tone={row.session === 'MORNING' ? 'warning' : 'info'}>
              <span className="inline-flex items-center gap-1">
                {EXAM_SESSION_LABEL[row.session]}
              </span>
            </Badge>
            <span className="whitespace-nowrap text-xs font-medium text-ink-2">
              {formatTimeRange(row.startTime, row.endTime)}
            </span>
          </div>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <span
          className={cn(
            'flex items-center gap-1.5 whitespace-nowrap text-sm font-bold',
            EXAM_STATUS_TEXT[row.status],
          )}
          title={EXAM_STATUS_HINT[row.status]}
        >
          <span
            className={cn('h-2 w-2 shrink-0 rounded-full', EXAM_STATUS_DOT[row.status])}
            aria-hidden
          />
          {EXAM_STATUS_LABEL[row.status]}
        </span>
      ),
    },
    {
      key: 'students',
      header: 'Students',
      align: 'center',
      render: (row) => (
        <div className="leading-tight">
          <p className="text-sm font-bold tabular-nums text-ink">{row.totalStudents}</p>
          {/* Only where the two differ — "28 of 28" under a paper already
              marked Published is the same fact said twice. */}
          {/* `enteredCount` includes absences, so they are taken out and named
              apart — an absence is accounted for, not marked. */}
          {row.enteredCount < row.totalStudents && (
            <p className="text-2xs font-semibold tabular-nums text-ink-3">
              {row.enteredCount - row.absentCount} marked
              {row.absentCount > 0 && ` · ${row.absentCount} absent`}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'average',
      header: 'Average',
      render: (row) => {
        const share = sharePercent(row.averageMarks, row.maxMarks);
        if (row.averageMarks === null || share === null) return NOT_YET;

        return (
          <span className="flex items-baseline gap-1.5 whitespace-nowrap">
            <span className="text-sm font-bold tabular-nums text-ink">{row.averageMarks}</span>
            {/* Read against this paper's own pass mark, which the server has
                already resolved — a fixed half-marks rule would call a class
                failing on a paper their institute passes at 35. */}
            <span
              className={cn(
                'text-2xs font-bold tabular-nums',
                row.averageMarks >= row.passMarks
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-rose-600 dark:text-rose-400',
              )}
            >
              ({share}%)
            </span>
          </span>
        );
      },
    },
    {
      key: 'highest',
      header: 'Highest',
      align: 'center',
      hideOnMobile: true,
      render: (row) =>
        row.highestMarks === null ? (
          NOT_YET
        ) : (
          <span className="text-sm font-bold tabular-nums text-ink-2">{row.highestMarks}</span>
        ),
    },
    {
      key: 'lowest',
      header: 'Lowest',
      align: 'center',
      hideOnMobile: true,
      render: (row) =>
        row.lowestMarks === null ? (
          NOT_YET
        ) : (
          <span className="text-sm font-bold tabular-nums text-ink-2">{row.lowestMarks}</span>
        ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'center',
      sticky: 'right',
      stickyWidth: 90,
      render: (row) => (
        <div className="flex items-center justify-center gap-1.5">
          {/* A draft has no marks sheet yet: nobody has sat it. */}
          {row.status !== 'DRAFT' && (
            <button
              type="button"
              onClick={() => openSheet(row)}
              aria-label={`Open the marks sheet for ${row.name}`}
              title={marksLabel(row)}
              className="grid h-8 w-8 place-items-center rounded-field border border-line bg-surface text-ink-2 transition-colors hover:border-accent hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line"
            >
              <Eye className="h-4 w-4" aria-hidden />
            </button>
          )}

          <Menu
            label={`More actions for ${row.name}`}
            trigger={<MoreVertical className="h-4 w-4" aria-hidden />}
            triggerClassName="h-8 w-8 border border-line bg-surface text-ink-2 hover:border-accent hover:text-accent"
            items={[
              // Only whoever set a class test publishes, edits or suspends it;
              // an institute exam is the office's. Marks follow the subject.
              row.status === 'DRAFT' && row.canEdit
                ? {
                    key: 'publish',
                    label: publishing === row.uuid ? 'Publishing…' : 'Publish',
                    description: 'The class can see it, and marks can be entered.',
                    icon: <Send className="h-4 w-4" />,
                    disabled: publishing !== null,
                    onSelect: () => void publish(row),
                  }
                : {
                    key: 'marks',
                    label: marksLabel(row),
                    icon: <FileSpreadsheet className="h-4 w-4" />,
                    onSelect: () => openSheet(row),
                  },
              ...(row.canEdit
                ? [
                    {
                      key: 'edit',
                      label: 'Edit paper',
                      icon: <Pencil className="h-4 w-4" />,
                      onSelect: () => startEditing(row),
                    },
                    {
                      key: 'suspend',
                      label: 'Suspend paper',
                      description:
                        'Stops it appearing for the class. Marks already entered are kept.',
                      icon: <ClipboardList className="h-4 w-4" />,
                      tone: 'danger' as const,
                      onSelect: () => setDeleting(row),
                    },
                  ]
                : []),
            ]}
          />
        </div>
      ),
    },
  ];

  // A suspended paper's sheet is closed and it cannot be edited, so its tab
  // offers nothing to do against it.
  const tableColumns = columns.filter((column) => {
    if (isSuspendedTab && column.key === 'actions') return false;
    return true;
  });

  return (
    <div className="flex flex-col gap-5">
      {/* --- 1. PAGE HEADER --------------------------------------------------- */}
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-black tracking-tight text-ink">Exam and Marks</h1>
          <p className="mt-1 text-sm font-medium text-ink-2">
            Set papers, enter marks and see how the class is doing.
          </p>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {/* Every subject's marks for their own class, side by side - the
              class teacher's review before the report cards. */}
          {classTeacherSections.length > 0 && (
            <Button
              variant="secondary"
              leftIcon={<LayoutGrid className="h-4 w-4" />}
              onClick={() => navigate(withExamOrigin('/teacher/exams/results', originTab))}
            >
              Class results
            </Button>
          )}
          {originTab === 'CLASS_TEST' && (
            <FormToggleButton
              isOpen={isFormOpen}
              className="text-[14px] font-semibold "
              label="Set a paper"
              controls="set-paper-form"
              onToggle={() => (isFormOpen ? closeForm() : openNewPaper())}
            />
          )}
        </div>
      </header>

      <Tabs
        label="Who set the papers"
        options={[
          { value: 'CLASS_TEST', label: 'Class tests', badge: originCounts.CLASS_TEST },
          { value: 'INSTITUTE', label: 'Institute tests', badge: originCounts.INSTITUTE },
        ]}
        value={originTab}
        onChange={changeOrigin}
      />

      <div
        id={tabPanelId(originTab)}
        role="tabpanel"
        aria-labelledby={`${originTab}-tab`}
        className="flex flex-col gap-5"
      >
        {originTab === 'INSTITUTE' && (
          <p className="text-xs font-medium text-ink-3">
            Set by your school office — Summative and Formative Assessments and the like. You can
            enter marks for the subjects you teach; only the office can change or suspend these
            papers.
          </p>
        )}

        {/* --- 2. HEADLINE FIGURES ---------------------------------------------- */}
        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 sm:gap-3.5 xl:grid-cols-5">
          <MetricTile
            tone="violet"
            icon={<ClipboardList className="h-4 w-4 sm:h-5 sm:w-5" />}
            label="Total Papers"
            value={papers.length}
            hint={
              hasFilters
                ? 'Matching your filters'
                : originTab === 'INSTITUTE'
                  ? 'Set by the school office'
                  : 'Class tests'
            }
            isLoading={isLoading}
          />
          <MetricTile
            tone="blue"
            icon={<Users className="h-4 w-4 sm:h-5 sm:w-5" />}
            label="Total Students"
            value={rosterSize}
            hint={classSectionUuid ? 'On this roster' : 'Across your classes'}
          />
          <MetricTile
            tone="emerald"
            icon={<CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5" />}
            label="Papers Completed"
            value={statusCounts.MARKED}
            hint={`of ${papers.length} set`}
            isLoading={isLoading}
            trailing={
              papers.length > 0 ? (
                <PercentRing
                  value={(statusCounts.MARKED / papers.length) * 100}
                  label="Papers fully marked"
                  toneClassName="text-emerald-500"
                  size={44}
                />
              ) : undefined
            }
          />
          <MetricTile
            tone="amber"
            icon={<Timer className="h-4 w-4 sm:h-5 sm:w-5" />}
            label="Upcoming Papers"
            value={statusCounts.SCHEDULED}
            hint="Not sat yet"
            isLoading={isLoading}
          />
          <MetricTile
            className="col-span-2 sm:col-span-1"
            tone="indigo"
            icon={<BarChart3 className="h-4 w-4 sm:h-5 sm:w-5" />}
            label="Average Score"
            value={averageScore === null ? '—' : `${averageScore.percent}%`}
            // Any paper with a mark in counts, finished or not, so it does not
            // claim the papers are "marked".
            hint={
              averageScore === null
                ? 'Nothing marked yet'
                : `Across ${averageScore.papers} ${averageScore.papers === 1 ? 'paper' : 'papers'} with marks in`
            }
            isLoading={isLoading}
          />
        </div>

        {/* --- 3. THE FORM ------------------------------------------------------ */}
        {isFormOpen && (
          <form
            id="set-paper-form"
            onSubmit={onSubmit}
            noValidate
            className="card flex flex-col gap-4 rounded-[16px] p-5"
          >
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line pb-3">
              <div>
                <h3 className="text-base font-bold text-ink">
                  {editing ? 'Edit paper' : 'Set a paper'}
                </h3>
                <p className="text-sm text-ink-3">
                  {editing
                    ? 'The class and subject stay as they are. Marks already entered are kept.'
                    : 'Marks are entered on the sheet once the paper exists.'}
                </p>
              </div>
              {editing && (
                <Badge tone="accent">
                  {`${editing.classSection.displayName} · ${editing.subject.name}`}
                </Badge>
              )}
            </div>

            {/* Who sits it. Only on a new paper: an existing one stays with its
                class and subject, because its marks belong to those children. */}
            {!editing && (
              <div className="flex flex-col gap-3">
                <div className="grid gap-4 sm:grid-cols-2">
                  <Select
                    label="Subject"
                    options={formSubjectOptions}
                    placeholder="Choose a subject"
                    error={errors.subjectUuid?.message}
                    {...register('subjectUuid')}
                  />
                </div>
                {formSectionOptions.length > 0 && (
                  <>
                    <CheckboxGroup
                      label="Sections"
                      columns={3}
                      options={formSectionOptions}
                      value={formSections}
                      onChange={chooseSections}
                      error={errors.classSectionUuid ? 'Choose at least one section' : undefined}
                      hint={
                        formSections.length > 1
                          ? `One paper is set for each of the ${formSections.length} sections, with the same name, total and date. Marks are entered per section.`
                          : 'Only the sections you teach this subject in are listed.'
                      }
                    />
                    {wholeClassShortcuts.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1.5">
                        {wholeClassShortcuts.map(({ grade, uuids }) => (
                          <button
                            key={grade}
                            type="button"
                            onClick={() =>
                              chooseSections([...new Set([...formSections, ...uuids])])
                            }
                            className="pill bg-surface-2 text-[12px] text-ink-2 transition-colors hover:bg-accent-soft hover:text-accent"
                          >
                            All of {grade} ({uuids.length})
                          </button>
                        ))}
                        {formSections.length > 0 && (
                          <button
                            type="button"
                            onClick={() => chooseSections([])}
                            className="pill bg-surface-2 text-[12px] text-ink-2 transition-colors hover:bg-accent-soft hover:text-accent"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="Paper name"
                placeholder="Unit Test I"
                error={errors.name?.message}
                {...register('name')}
              />
              <Select
                label="Kind of paper"
                options={EXAM_TYPE_OPTIONS}
                hint={selectedType ? EXAM_TYPE_HINT[selectedType] : undefined}
                error={errors.examType?.message}
                {...register('examType')}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <Input
                label="Out of"
                placeholder="25"
                inputMode="numeric"
                error={errors.maxMarks?.message}
                {...register('maxMarks')}
              />
              <Input
                label="Pass mark (optional)"
                placeholder="Institute default"
                inputMode="numeric"
                hint="Leave empty to use your institute's own pass percentage."
                error={errors.passMarks?.message}
                {...register('passMarks')}
              />
              <DatePicker
                label="Held on"
                min={earliestExamDate(editing?.examDate)}
                error={errors.examDate?.message}
                {...register('examDate')}
              />
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-line pt-3">
              <Button variant="ghost" type="button" onClick={closeForm} disabled={isSubmitting}>
                Cancel
              </Button>
              {/* A draft can be saved again as a draft; a published paper cannot go back. */}
              {(!editing || isDraft) && (
                <Button
                  type="submit"
                  name="publication"
                  value="DRAFT"
                  variant="secondary"
                  leftIcon={<FilePen className="h-4 w-4" />}
                  disabled={isSubmitting && savingAs !== 'DRAFT'}
                  isLoading={savingAs === 'DRAFT'}
                >
                  {editing ? 'Save draft' : 'Save as draft'}
                </Button>
              )}
              <Button
                type="submit"
                name="publication"
                value="PUBLISHED"
                leftIcon={<Send className="h-4 w-4" />}
                disabled={isSubmitting && savingAs !== 'PUBLISHED'}
                isLoading={savingAs === 'PUBLISHED'}
              >
                {!editing ? 'Set paper' : isDraft ? 'Publish' : 'Save changes'}
              </Button>
            </div>
          </form>
        )}

        {/* --- 4. THE LIST ------------------------------------------------------ */}
        {error ? (
          <QueryError title="Could not load your papers" error={error} onRetry={refetch} />
        ) : (
          <section className="card overflow-hidden rounded-[16px] border border-line bg-surface shadow-2xs">
            <div className="px-4 pt-3 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
              <Tabs
                label="Papers by standing"
                options={EXAM_STATUS_FILTERS.map((value) => ({
                  value,
                  label: EXAM_STATUS_TAB_LABEL[value],
                  badge: statusCounts[value],
                }))}
                value={statusTab}
                onChange={(status) => updateFilters({ status })}
              />
            </div>

            {/* Filters. The first four are the server's; the search box narrows
              what has already arrived, so it needs no round trip. */}
            <div className="flex flex-col gap-3 border-b border-line p-4 xl:flex-row xl:items-end">
              <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <Select
                  label="Class"
                  options={classOptions}
                  value={classSectionUuid}
                  onChange={(event) => updateFilters({ classSectionUuid: event.target.value })}
                  className="h-9 text-[13px] font-semibold"
                />
                {/* Only worth asking with two subjects to tell apart — the first option is "All subjects". */}
                {subjectOptions.length > 2 && (
                  <Select
                    label="Subject"
                    options={subjectOptions}
                    value={filters.subjectUuid}
                    onChange={(event) => updateFilters({ subjectUuid: event.target.value })}
                    className="h-9 text-[13px] font-semibold"
                  />
                )}
                <Select
                  label="Kind"
                  options={EXAM_TYPE_FILTER_OPTIONS}
                  value={filters.examType}
                  onChange={(event) =>
                    updateFilters({ examType: event.target.value as Filters['examType'] })
                  }
                  className="h-9 text-[13px] font-semibold"
                />
                <DatePicker
                  label="Held on"
                  isClearable
                  size="sm"
                  placeholder="Any day"
                  value={filters.examDate}
                  onChange={(event) => updateFilters({ examDate: event.target.value })}
                  className="h-9 text-[13px] font-semibold"
                />
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-2.5 w-full xl:w-auto">
                <div className="w-full sm:w-56">
                  <Input
                    label="Search papers"
                    hideLabel
                    type="search"
                    value={search}
                    onChange={(event) => updateFilters({ search: event.target.value })}
                    placeholder="Search papers..."
                    leftIcon={<Search className="h-3.5 w-3.5" />}
                    className="h-9 w-full text-[13px]"
                  />
                </div>

                <Button
                  variant={Boolean(hasFilters || search) ? 'dangerSoft' : 'secondary'}
                  size="sm"
                  disabled={!hasFilters && !search}
                  leftIcon={
                    <X
                      className={cn(
                        'h-3.5 w-3.5 transition-colors',
                        hasFilters || search
                          ? 'text-rose-600 dark:text-rose-400'
                          : 'text-ink-3 opacity-60',
                      )}
                    />
                  }
                  className={cn(
                    'h-9 px-2.5 shrink-0 text-xs font-semibold transition-colors',
                    hasFilters || search
                      ? 'border-rose-500/40 bg-rose-500/10 text-rose-600 hover:border-rose-500 hover:bg-rose-500/20 hover:text-rose-700 dark:border-rose-500/40 dark:text-rose-400 dark:hover:bg-rose-500/20'
                      : 'opacity-50 cursor-not-allowed',
                  )}
                  onClick={() => {
                    if (!hasFilters && !search) return;
                    // The standing tab is where the reader is, not a filter, so it stays.
                    updateFilters({
                      classSectionUuid: '',
                      subjectUuid: '',
                      examType: '',
                      examDate: '',
                      search: '',
                    });
                  }}
                >
                  Clear
                </Button>
              </div>
            </div>

            <div id={tabPanelId(statusTab)} role="tabpanel" aria-labelledby={`${statusTab}-tab`}>
              {isSuspendedTab && suspendedError ? (
                <div className="p-4">
                  <QueryError
                    title="Could not load your suspended papers"
                    error={suspendedError}
                    onRetry={refetchSuspended}
                  />
                </div>
              ) : (
                <Table
                  variant="plain"
                  columns={tableColumns}
                  rows={pageRows}
                  rowKey={(row) => row.uuid}
                  isLoading={isSuspendedTab ? isSuspendedLoading : isLoading}
                  empty={
                    <EmptyState
                      icon={<ClipboardList className="h-5 w-5" />}
                      title={
                        isSuspendedTab && listed.length === 0
                          ? 'No suspended papers'
                          : listed.length === 0
                            ? hasFilters
                              ? 'No papers match'
                              : 'No papers set yet'
                            : 'Nothing under this tab'
                      }
                      description={
                        isSuspendedTab && listed.length === 0
                          ? hasFilters
                            ? 'No suspended paper matches those filters.'
                            : 'A paper you suspend is listed here, with the marks already entered against it.'
                          : listed.length === 0
                            ? hasFilters
                              ? 'Nothing matches those filters. Clear them to see everything you have set.'
                              : 'Set your first paper — a unit test, a practical or the half-yearly — then enter its marks on the sheet.'
                            : search
                              ? 'Nothing matches that search. Clear it to see the rest of this tab.'
                              : `${EXAM_STATUS_TAB_HINT[statusTab]} None of your papers stands there right now — the other tabs show the rest.`
                      }
                    />
                  }
                />
              )}
            </div>

            {rows.length > 0 && (
              <Pagination
                meta={{
                  page: currentPage,
                  limit: filters.limit,
                  total: rows.length,
                  totalPages,
                }}
                onPageChange={(next) => setFilters({ page: next })}
                itemLabel={rows.length === 1 ? 'paper' : 'papers'}
                showPageNumbers
                limitOptions={PAGE_SIZES}
                onLimitChange={changePageSize}
              />
            )}
          </section>
        )}
      </div>

      <ConfirmDialog
        isOpen={Boolean(deleting)}
        tone="danger"
        title={`Suspend ${deleting?.name ?? 'this paper'}?`}
        description={
          deleting
            ? `Nothing is deleted. It stops appearing for ${deleting.classSection.displayName}, and the ${deleting.enteredCount} ${deleting.enteredCount === 1 ? 'mark' : 'marks'} already entered are kept.`
            : undefined
        }
        confirmLabel="Suspend paper"
        isLoading={isDeleting}
        onConfirm={confirmDelete}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
}
